import { fetchCurrentUser } from '../../ducks/user.duck';
import { ShopifyApi } from '../../util/api';
import { denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';

import { types as sdkTypes } from '../../util/sdkLoader';
import { createListingDataArray } from '../../util/editListingHelpers';
import { queryOwnListings } from '../ShopSettingsPage/ShopSettingsPage.duck';
const { Money } = sdkTypes;
const RESULT_PAGE_SIZE = 30;

// ================ Action types ================ //

const ActionTypes = {
  SET_INITIAL_STATE: 'app/ShopifyAccountPage/SET_INITIAL_STATE',

  UPLOAD_IMAGE_REQUEST: 'app/ShopifyAccountPage/UPLOAD_IMAGE_REQUEST',
  UPLOAD_IMAGE_SUCCESS: 'app/ShopifyAccountPage/UPLOAD_IMAGE_SUCCESS',
  UPLOAD_IMAGE_ERROR: 'app/ShopifyAccountPage/UPLOAD_IMAGE_ERROR',

  CREATE_PUBLISH_REQUEST: 'app/ShopifyAccountPage/CREATE_PUBLISH_REQUEST',
  CREATE_PUBLISH_SUCCESS: 'app/ShopifyAccountPage/CREATE_PUBLISH_SUCCESS',
  CREATE_PUBLISH_ERROR: 'app/ShopifyAccountPage/CREATE_PUBLISH_ERROR',
  CREATE_PUBLISH_COMPLETE: 'app/ShopifyAccountPage/ShopifyAccountPage',

  GENERATE_SHOPIFY_AUTH_URL_REQUEST: 'app/ShopifyAccountPage/GENERATE_SHOPIFY_AUTH_URL_REQUEST',
  GENERATE_SHOPIFY_AUTH_URL_SUCCESS: 'app/ShopifyAccountPage/GENERATE_SHOPIFY_AUTH_URL_SUCCESS',
  GENERATE_SHOPIFY_AUTH_URL_ERROR: 'app/ShopifyAccountPage/GENERATE_SHOPIFY_AUTH_URL_ERROR',

  SET_STOCK_REQUEST: 'app/ShopifyAccountPage/SET_STOCK_REQUEST',
  SET_STOCK_SUCCESS: 'app/ShopifyAccountPage/SET_STOCK_SUCCESS',
  SET_STOCK_ERROR: 'app/ShopifyAccountPage/SET_STOCK_ERROR',
};

// ================ Initial State ================ //

const initialState = {
  fetchListingInProgress: false,
  fetchListingError: null,
  listings: [],
  uploadImageError: null,
  uploadInProgress: false,
  reviews: [],
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  ownEntities: {},
  createDraftInProgress: false,
  createPublishError: null,
  generateAuthUrlInProgress: false,
  generateAuthUrlError: null,
  authUrl: null,
  handleCallbackInProgress: false,
  handleCallbackError: null,
  isShopifyConnected: false,
  setStockInProgress: false,
  setStockError: null,
  createDraftsComplete: false,
};

// ================ Reducers ================ //

const ShopifyAccountPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.UPLOAD_IMAGE_REQUEST:
      return { ...state, uploadInProgress: true, uploadImageError: null };
    case ActionTypes.UPLOAD_IMAGE_SUCCESS:
      return { ...state, uploadInProgress: false };
    case ActionTypes.UPLOAD_IMAGE_ERROR:
      return { ...state, uploadInProgress: false, uploadImageError: payload };

    case ActionTypes.CREATE_PUBLISH_REQUEST:
      return { ...state, createDraftInProgress: true, createPublishError: null };
    case ActionTypes.CREATE_PUBLISH_SUCCESS:
      return { ...state, createDraftInProgress: false };
    case ActionTypes.CREATE_PUBLISH_ERROR:
      return { ...state, createDraftInProgress: false, createPublishError: payload };

    case ActionTypes.CREATE_PUBLISH_COMPLETE:
      return { ...state, createDraftsComplete: true };

    case ActionTypes.GENERATE_SHOPIFY_AUTH_URL_REQUEST:
      return { ...state, generateAuthUrlInProgress: true, generateAuthUrlError: null };
    case ActionTypes.GENERATE_SHOPIFY_AUTH_URL_SUCCESS:
      return { ...state, generateAuthUrlInProgress: false, authUrl: payload.authUrl };
    case ActionTypes.GENERATE_SHOPIFY_AUTH_URL_ERROR:
      return { ...state, generateAuthUrlInProgress: false, generateAuthUrlError: payload };

    case ActionTypes.SET_STOCK_REQUEST:
      return { ...state, setStockInProgress: true, setStockError: null };
    case ActionTypes.SET_STOCK_SUCCESS:
      return { ...state, setStockInProgress: false };
    case ActionTypes.SET_STOCK_ERROR:
      return { ...state, setStockInProgress: false, setStockError: payload };

    default:
      return state;
  }
};

// ================ Selectors ================ //

export const uploadImageInProgressSelector = state => state.ShopifyAccountPage.uploadInProgress;

export const queryInProgressSelector = state => state.ShopifyAccountPage.queryInProgress;

export const queryParamsSelector = state => state.ShopifyAccountPage.queryParams;
export const createDraftsCompleteSelector = state => state.ShopifyAccountPage.createDraftsComplete;

export const createDraftInProgressSelector = state =>
  state.ShopifyAccountPage.createDraftInProgress;

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ShopifyAccountPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Actions Creators ================ //

const uploadImageRequest = () => ({ type: ActionTypes.UPLOAD_IMAGE_REQUEST });
const uploadImageSuccess = () => ({ type: ActionTypes.UPLOAD_IMAGE_SUCCESS });
const uploadImageError = error => ({
  type: ActionTypes.UPLOAD_IMAGE_ERROR,
  error: true,
  payload: error,
});

const createPublishRequest = () => ({ type: ActionTypes.CREATE_PUBLISH_REQUEST });
const createPublishSuccess = () => ({ type: ActionTypes.CREATE_PUBLISH_SUCCESS });
const createPublishError = e => ({
  type: ActionTypes.CREATE_PUBLISH_ERROR,
  error: true,
  payload: e,
});

export const generateShopifyAuthUrlRequest = () => ({
  type: ActionTypes.GENERATE_SHOPIFY_AUTH_URL_REQUEST,
});
export const generateShopifyAuthUrlSuccess = authUrl => ({
  type: ActionTypes.GENERATE_SHOPIFY_AUTH_URL_SUCCESS,
  payload: { authUrl },
});
export const generateShopifyAuthUrlError = error => ({
  type: ActionTypes.GENERATE_SHOPIFY_AUTH_URL_ERROR,
  error: true,
  payload: error,
});

export const setStockRequest = () => ({ type: ActionTypes.SET_STOCK_REQUEST });
export const setStockSuccess = () => ({ type: ActionTypes.SET_STOCK_SUCCESS });
export const setStockError = e => ({ type: ActionTypes.SET_STOCK_ERROR, error: true, payload: e });

export const setInitialState = () => ({
  type: ActionTypes.SET_INITIAL_STATE,
});

export const createPublishComplete = () => ({
  type: ActionTypes.CREATE_PUBLISH_COMPLETE,
});

// ================ Thunks ================ //

export const uploadImage = actionPayload => {
  return async (dispatch, getState, sdk) => {
    const { file } = actionPayload;

    if (!file) {
      throw new Error('No file provided for upload');
    }

    dispatch(uploadImageRequest());

    const bodyParams = { image: file };
    const queryParams = {
      expand: true,
    };

    try {
      const resp = await sdk.images.upload(bodyParams, queryParams);
      const {
        data: { data: uploadedImage },
      } = resp;

      if (!uploadedImage) {
        throw new Error('Upload response contains no image data');
      }

      const imageUrl = uploadedImage.attributes?.variants?.default?.url;
      if (!imageUrl) {
        throw new Error('No valid URL found in uploaded image variants');
      }
      const uploadedImageId = uploadedImage.id;
      dispatch(uploadImageSuccess());
      return uploadedImageId;
    } catch (e) {
      const error = storableError(e);
      dispatch(uploadImageError(error));
      throw error;
    }
  };
};

const updateStockOfListingMaybe = (listingId, stockTotals, dispatch) => {
  const { oldTotal, newTotal } = stockTotals || {};
  // Note: newTotal and oldTotal must be given, but oldTotal can be null
  const hasStockTotals = newTotal >= 0 && typeof oldTotal !== 'undefined';

  if (listingId && hasStockTotals) {
    return dispatch(compareAndSetStock(listingId, oldTotal, newTotal));
  }
  return Promise.resolve();
};

export const compareAndSetStock = (listingId, oldTotal, newTotal) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(setStockRequest());

    // Update stock using SDK with compareAndSet
    const response = await sdk.stock.compareAndSet(
      { listingId, oldTotal, newTotal },
      { expand: true }
    );

    // Update client app's state with the response
    dispatch(setStockSuccess());
  } catch (e) {
    log.error(e, 'update-stock-failed', { listingId, oldTotal, newTotal });
    dispatch(setStockError(storableError(e)));
  }
};
export const createListings = ({ products, config, shop, country }) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(createPublishRequest());

    const productsData = await createListingDataArray({
      products,
      dispatch,
      sdk,
      shop,
      country,
    });

    // Helper function to delay execution (for rate-limiting)
    const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

    // Process listings in batches of 40 per minute (1.5 seconds per listing)
    const BATCH_SIZE = 40;
    const TIME_PER_LISTING = 1500; // 60 seconds / 40 = 1.5 seconds

    for (let i = 0; i < productsData.length; i += BATCH_SIZE) {
      const batch = productsData.slice(i, i + BATCH_SIZE);

      // Process each listing in the batch
      for (const [index, product] of batch.entries()) {
        // Create listingData by excluding local control fields that Sharetribe create disallows.
        const { stockUpdate, state: desiredState, ...listingData } = product;

        // Create the listing
        const createResponse = await sdk.ownListings.create(listingData, {
          expand: true,
        });
        const listingId = createResponse?.data?.data?.id;

        if (listingId) {
          // Update stock after listing creation
          await updateStockOfListingMaybe(listingId, stockUpdate, dispatch);

          if (desiredState === 'published') {
            await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });
          }
        }

        // Delay to enforce rate limit (skip delay for the last item in the batch)
        if (index < batch.length - 1 || i + index < productsData.length - 1) {
          await sleep(TIME_PER_LISTING);
        }
      }
    }

    // Dispatch completion action after all listings are created
    dispatch(createPublishComplete());
    // Dispatch success for all listings
    dispatch(createPublishSuccess());
    await dispatch(
      queryOwnListings({
        config,
        states: 'published,closed,draft',
        page: 1,
        perPage: RESULT_PAGE_SIZE,
        fetchAll: true,
      })
    );
  } catch (e) {
    dispatch(createPublishError(storableError(e)));
    throw e;
  }
};

export const generateShopifyAuthUrl = (shopUrl, userId) => {
  return async (dispatch, getState, sdk) => {
    dispatch(generateShopifyAuthUrlRequest());
    try {
      const response = await ShopifyApi.generateShopifyAuthUrl({
        shop: shopUrl,
        userId,
      });

      const { authUrl } = response.data;
      if (authUrl) {
        dispatch(generateShopifyAuthUrlSuccess(authUrl));
        window.location.href = authUrl; // Redirect to Shopify auth
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      dispatch(generateShopifyAuthUrlError(storableError(error)));
      throw error;
    }
  };
};

// Thunks
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch(setInitialState());
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1 } = queryParams || {};
  const currentUser = await dispatch(fetchCurrentUser({ enforce: true }));

  const listingsPromise = dispatch(
    queryOwnListings({
      config,
      states: 'published,closed,draft',
      page,
      perPage: RESULT_PAGE_SIZE,
      fetchAll: true,
    })
  );
  await listingsPromise;
  return currentUser;
};

export default ShopifyAccountPageReducer;
